import React, { useEffect, useRef, useState } from 'react'
import { lookupSteps, lookupNotes } from './lookup-model'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

const REVEAL_MS = 120
const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

// The Look up button and its results, anchored under the issuer field.
// Discovery is one request: while it is in flight the bar is indeterminate
// and the first step spins; when it lands, the steps resolve in order from
// what the response actually says.
export const LookupPanel = ({ issuer, onLookup, onApply, usedClaims = [], onJump }) => {
  const [phase, setPhase] = useState('idle') // idle | running | done | failed
  const [steps, setSteps] = useState([])
  const [result, setResult] = useState(null) // { discovery, notes, at }
  const [error, failure, setError] = useErrorLine()
  const [lookedUpIssuer, setLookedUpIssuer] = useState(null)
  const panel = useRef(null)
  const runId = useRef(0)

  useEffect(() => { if (phase !== 'idle' && panel.current && panel.current.scrollIntoView) panel.current.scrollIntoView({ block: 'nearest' }) }, [phase])

  // Unmounting, or starting a newer run, invalidates this run's id: a lookup
  // that resolves after that must not touch state or call onApply — the
  // form it would fill may already be gone.
  useEffect(() => () => { runId.current++ }, [])

  const run = async () => {
    const id = ++runId.current
    setPhase('running'); setError(null); setResult(null)
    setSteps([{ key: 'fetched', label: 'Fetching the discovery document', status: 'running' }])
    let discovery
    try {
      discovery = await onLookup(issuer)
    } catch (exp) {
      if (runId.current !== id) return
      setSteps([{ key: 'fetched', label: 'Could not fetch the discovery document', status: 'fail' }])
      setError((exp && exp.data && exp.data.message) || 'The identity provider could not be reached.', exp)
      setLookedUpIssuer(issuer)
      setPhase('failed')
      return
    }
    if (runId.current !== id) return
    const final = lookupSteps(discovery)
    for (let i = 0; i < final.length; i++) {
      setSteps([...final.slice(0, i + 1), ...(i + 1 < final.length ? [{ ...final[i + 1], status: 'running' }] : [])])
      if (i + 1 < final.length) {
        await wait(REVEAL_MS)
        if (runId.current !== id) return
      }
    }
    if (runId.current !== id) return
    const filled = onApply(discovery) || []
    setResult({ discovery, notes: lookupNotes({ discovery, filled, usedClaims }), at: new Date() })
    setLookedUpIssuer(issuer)
    setPhase('done')
  }

  // A failure is about the issuer it tried, too: once the field changes (an
  // edit, or Discard), neither result describes what is typed any more.
  const stale = (phase === 'done' || phase === 'failed') && lookedUpIssuer !== issuer
  const buttonLabel = phase === 'running' ? 'Looking up…' : phase === 'idle' ? 'Look up' : 'Look up again'

  return (
    <React.Fragment>
      <button type="button" className="btn sw-btn-primary text-nowrap" onClick={run} disabled={phase === 'running' || !issuer}>{buttonLabel}</button>

      {phase !== 'idle' &&
        <div className="sw-lookup w-100" ref={panel}>
          <div className="sw-lookup-head">
            {/* Only the status line is announced; the steps and facts below
                are there to read, not to be read out on every change. */}
            <strong className="small" role="status" aria-live="polite">
              {phase === 'running' && 'Checking the identity provider…'}
              {phase === 'failed' && (stale ? 'Out of date: the issuer changed. Look up again.' : 'Lookup failed')}
              {phase === 'done' && (stale ? 'Out of date: the issuer changed. Look up again.' : `Looked up at ${result.at.toLocaleTimeString()}`)}
            </strong>
            {phase !== 'running' && <button type="button" className="btn btn-sm sw-btn" onClick={() => setPhase('idle')}>Hide</button>}
          </div>
          <div className={`sw-bar ${phase === 'running' ? 'sw-running' : 'sw-done'}`}><i /></div>

          <ol className="sw-steps">
            {steps.map(step =>
              <li key={step.key} data-status={step.status}><span className="sw-ic" />{step.label}{step.detail && <span className="small ms-1">({step.detail})</span>}</li>)}
          </ol>

          {phase === 'failed' &&
            <div className="sw-out sw-failed">
              <span>{error}</span>
              <ErrorReference error={failure} className="mt-0" />
              <span className="sw-hint">Check the issuer. For Microsoft it ends in /v2.0; you can also paste the full .well-known/openid-configuration address.</span>
              <div><button type="button" className="btn sw-btn btn-sm" onClick={run}>Try again</button></div>
            </div>}

          {phase === 'done' &&
            <div className="sw-out">
              <div className="sw-facts">
                <div className="sw-fact"><span className="sw-k">issuer</span><span className="sw-v">{result.discovery.issuerMatches ? 'matches exactly' : result.discovery.documentIssuer}</span></div>
                {result.discovery.tenantValue && <div className="sw-fact"><span className="sw-k">tenant</span><span className="sw-v">{result.discovery.tenantValue}</span></div>}
                <div className="sw-fact"><span className="sw-k">sign-out endpoint</span><span className="sw-v">{result.discovery.endSessionEndpoint ? `published${result.discovery.logoutFrameBlocked ? ' · blocks embedding' : ''}` : 'not published'}</span></div>
                <div className="sw-fact"><span className="sw-k">sign-in methods</span><span className="sw-v">{(result.discovery.authMethods || []).join(' · ') || 'not published'}</span></div>
                <div className="sw-fact"><span className="sw-k">scopes</span><span className="sw-v">{(result.discovery.scopesSupported || []).join(' · ') || 'not published'}</span></div>
              </div>
              <ul className="sw-notes">
                {result.notes.map((note, i) =>
                  <li key={i} data-tone={note.tone}>
                    <span>{note.text}{note.jump && onJump && <React.Fragment> <a href={`#${note.jump}`} onClick={e => { e.preventDefault(); onJump(note.jump) }}>See claims</a></React.Fragment>}</span>
                  </li>)}
              </ul>
            </div>}
        </div>}
    </React.Fragment>
  )
}
